import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { exportSubSellerPayoutCsv } from '../../lib/csv-export';

interface SubSellerPayoutItem {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  totalPayinAmount: number;
  totalPayoutAmount: number;
  myPayinShare: number;
  myPayoutShare: number;
  myPayinSharePercent: number;
  myPayoutSharePercent: number;
  status: 'unpaid' | 'paid';
  paymentMethod: string | null;
  paidAt: string | null;
}

export function SubSellerPayoutReports() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['sub-seller', 'payouts'],
    queryFn: () =>
      apiGet<{ items: SubSellerPayoutItem[] }>('/sub-seller/payouts', { pageSize: '50' }),
  });

  const payouts = data?.ok ? data.data.items : [];

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        {payouts.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => exportSubSellerPayoutCsv(payouts, t)}>
            {t('payouts.exportCsv')}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && payouts.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {payouts.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payouts.period')}</TableHead>
                  <TableHead>{t('payouts.periodStart')}</TableHead>
                  <TableHead>{t('payouts.periodEnd')}</TableHead>
                  <TableHead className="text-right">{t('sellers.payinFee')} %</TableHead>
                  <TableHead className="text-right text-blue-600 dark:text-blue-400">
                    {t('transactions.calcPayinFee')}
                  </TableHead>
                  <TableHead className="text-right">{t('sellers.payoutFee')} %</TableHead>
                  <TableHead className="text-right text-green-600 dark:text-green-400">
                    {t('transactions.calcPayoutFee')}
                  </TableHead>
                  <TableHead>{t('payouts.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell className="capitalize">{payout.period}</TableCell>
                    <TableCell>{payout.startDate}</TableCell>
                    <TableCell>{payout.endDate}</TableCell>
                    <TableCell className="text-right">{payout.myPayinSharePercent}%</TableCell>
                    <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">
                      {formatNum(payout.myPayinShare)}
                    </TableCell>
                    <TableCell className="text-right">{payout.myPayoutSharePercent}%</TableCell>
                    <TableCell className="text-right font-medium text-green-600 dark:text-green-400">
                      {formatNum(payout.myPayoutShare)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={payout.status === 'paid' ? 'success' : 'warning'}>
                        {t(`payouts.${payout.status}`)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
